import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  Trash2, 
  RefreshCw, 
  AlertTriangle, 
  Users, 
  FileText, 
  Activity,
  Database,
  RotateCcw,
  Clock,
  Shield,
  Search,
  ChevronDown,
  ChevronRight,
  Loader2,
  CheckCircle,
  XCircle,
  Calendar,
  Archive,
  Zap
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterDelete() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // User deletion state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  
  // Session deletion state
  const [sessionFilters, setSessionFilters] = useState({
    status: "",
    beforeDate: "",
    userId: ""
  });
  
  // Recycle bin state
  const [recycleBin, setRecycleBin] = useState({ items: [], total: 0, breakdown: {} });
  const [recycleFilter, setRecycleFilter] = useState("");
  const [recyclePage, setRecyclePage] = useState(1);
  
  // Confirmation dialogs
  const [deleteUserDialog, setDeleteUserDialog] = useState(null);
  const [deleteCollectionDialog, setDeleteCollectionDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [purgeDialog, setPurgeDialog] = useState(false);
  
  // Operation state
  const [operationLoading, setOperationLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/master-delete/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
      toast.error("Failed to load database statistics");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecycleBin = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (recycleFilter) params.append("collection_filter", recycleFilter);
      params.append("page", recyclePage);
      params.append("limit", 20);
      
      const response = await axios.get(`${API}/admin/recycle-bin?${params}`);
      setRecycleBin(response.data);
    } catch (error) {
      console.error("Failed to fetch recycle bin:", error);
    }
  }, [recycleFilter, recyclePage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (activeTab === "recycle-bin") {
      fetchRecycleBin();
    }
  }, [activeTab, fetchRecycleBin]);

  const handleDeleteUser = async (userId) => {
    setOperationLoading(true);
    try {
      const response = await axios.post(`${API}/admin/master-delete/user/${userId}`);
      toast.success(response.data.message);
      setDeleteUserDialog(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }
    
    setOperationLoading(true);
    try {
      for (const userId of selectedUsers) {
        await axios.post(`${API}/admin/master-delete/user/${userId}`);
      }
      toast.success(`${selectedUsers.length} users moved to recycle bin`);
      setSelectedUsers([]);
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete some users");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteSessions = async () => {
    const params = {};
    if (sessionFilters.status) params.status = sessionFilters.status;
    if (sessionFilters.beforeDate) params.before_date = sessionFilters.beforeDate;
    if (sessionFilters.userId) params.user_id = sessionFilters.userId;
    
    if (Object.keys(params).length === 0) {
      toast.error("Please select at least one filter");
      return;
    }
    
    setOperationLoading(true);
    try {
      const response = await axios.post(`${API}/admin/master-delete/sessions`, null, { params });
      toast.success(response.data.message);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete sessions");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteCollection = async (collectionName) => {
    setOperationLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/master-delete/collection/${collectionName}?confirm=true`
      );
      toast.success(response.data.message);
      setDeleteCollectionDialog(null);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete collection");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleFullReset = async () => {
    if (!resetPassword) {
      toast.error("Password required");
      return;
    }
    
    setOperationLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/master-delete/full-reset?confirm=true&password=${encodeURIComponent(resetPassword)}`
      );
      toast.success(response.data.message);
      setResetDialog(false);
      setResetPassword("");
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset database");
    } finally {
      setOperationLoading(false);
    }
  };

  const handleRestoreItem = async (itemId) => {
    try {
      const response = await axios.post(`${API}/admin/recycle-bin/restore/${itemId}`);
      toast.success(response.data.message);
      fetchRecycleBin();
      fetchStats();
    } catch (error) {
      toast.error("Failed to restore item");
    }
  };

  const handleRestoreUser = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/recycle-bin/restore-user/${userId}`);
      toast.success(response.data.message);
      fetchRecycleBin();
      fetchStats();
    } catch (error) {
      toast.error("Failed to restore user");
    }
  };

  const handlePermanentDelete = async (itemId) => {
    try {
      await axios.delete(`${API}/admin/recycle-bin/${itemId}`);
      toast.success("Item permanently deleted");
      fetchRecycleBin();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handlePurgeRecycleBin = async (forceAll = false) => {
    setOperationLoading(true);
    try {
      const response = await axios.delete(`${API}/admin/recycle-bin/purge?force_all=${forceAll}`);
      toast.success(response.data.message);
      setPurgeDialog(false);
      fetchRecycleBin();
      fetchStats();
    } catch (error) {
      toast.error("Failed to purge recycle bin");
    } finally {
      setOperationLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const filteredUsers = stats?.users_with_sessions?.filter(user =>
    user.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearchQuery.toLowerCase())
  ) || [];

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Trash2 className="w-7 h-7 text-red-500" />
            Master Delete
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Manage database cleanup with 30-day recovery option
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchStats(); if (activeTab === 'recycle-bin') fetchRecycleBin(); }}
          className="rounded-full"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-amber-800">Data Recovery Available</h3>
          <p className="text-amber-700 text-sm mt-1">
            All deleted items are moved to the Recycle Bin and can be recovered within 30 days.
            After 30 days, items are automatically marked for permanent deletion.
          </p>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="overview" className="rounded-full">
            <Database className="w-4 h-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-full">
            <Users className="w-4 h-4 mr-2" /> Users
          </TabsTrigger>
          <TabsTrigger value="sessions" className="rounded-full">
            <FileText className="w-4 h-4 mr-2" /> Sessions
          </TabsTrigger>
          <TabsTrigger value="collections" className="rounded-full">
            <Activity className="w-4 h-4 mr-2" /> Collections
          </TabsTrigger>
          <TabsTrigger value="recycle-bin" className="rounded-full">
            <Archive className="w-4 h-4 mr-2" /> Recycle Bin
            {stats?.recycle_bin?.total > 0 && (
              <Badge variant="secondary" className="ml-2 bg-red-100 text-red-700">
                {stats.recycle_bin.total}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#5C5C5C] flex items-center gap-2">
                  <Users className="w-4 h-4" /> Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#1A1A1A]">{stats?.collections?.users || 0}</div>
                <p className="text-xs text-[#9CA3AF] mt-1">
                  Including {stats?.collections?.admins || 0} admin(s)
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#5C5C5C] flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#1A1A1A]">{stats?.collections?.sessions || 0}</div>
                <p className="text-xs text-[#9CA3AF] mt-1">Presentation sessions</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#5C5C5C] flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Activity Logs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#1A1A1A]">{stats?.collections?.activity_logs || 0}</div>
                <p className="text-xs text-[#9CA3AF] mt-1">System activity records</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#5C5C5C] flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Access Logs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#1A1A1A]">{stats?.collections?.access_logs || 0}</div>
                <p className="text-xs text-[#9CA3AF] mt-1">User access records</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-[#5C5C5C] flex items-center gap-2">
                  <Zap className="w-4 h-4" /> LLM Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-[#1A1A1A]">{stats?.collections?.llm_usage || 0}</div>
                <p className="text-xs text-[#9CA3AF] mt-1">AI API call records</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md bg-red-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-2">
                  <Archive className="w-4 h-4" /> Recycle Bin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-700">{stats?.recycle_bin?.total || 0}</div>
                <p className="text-xs text-red-500 mt-1">
                  {stats?.recycle_bin?.expired || 0} expired (ready to purge)
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Full Reset Section */}
          <Card className="mt-6 border-red-200 bg-red-50/50">
            <CardHeader>
              <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Full Database Reset
              </CardTitle>
              <CardDescription className="text-red-600">
                Remove all data except admin users. All data will be moved to recycle bin for 30-day recovery.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="destructive" 
                onClick={() => setResetDialog(true)}
                className="rounded-full"
                data-testid="full-reset-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Reset Database
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-6">
          <Card className="border-none shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>User-wise Deletion</CardTitle>
                  <CardDescription>
                    Select users to delete along with all their sessions and activity logs
                  </CardDescription>
                </div>
                {selectedUsers.length > 0 && (
                  <Button 
                    variant="destructive" 
                    onClick={handleBulkDeleteUsers}
                    disabled={operationLoading}
                    className="rounded-full"
                  >
                    {operationLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete {selectedUsers.length} Selected
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  placeholder="Search users by name or email..."
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  className="pl-10 rounded-full"
                />
              </div>

              {/* User List */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-[#5C5C5C]">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No non-admin users found</p>
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <div 
                      key={user.id}
                      className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${
                        selectedUsers.includes(user.id) 
                          ? 'border-red-300 bg-red-50' 
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selectedUsers.includes(user.id)}
                          onCheckedChange={() => toggleUserSelection(user.id)}
                        />
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{user.name}</p>
                          <p className="text-sm text-[#5C5C5C]">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-[#1A1A1A]">{user.session_count} sessions</p>
                          <p className="text-xs text-[#9CA3AF]">{user.department || 'No department'}</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteUserDialog(user)}
                          className="rounded-full text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="mt-6">
          <Card className="border-none shadow-md">
            <CardHeader>
              <CardTitle>Session Deletion</CardTitle>
              <CardDescription>
                Delete sessions based on status, date, or user
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#5C5C5C] mb-2 block">Status</label>
                  <Select 
                    value={sessionFilters.status || "any"} 
                    onValueChange={(v) => setSessionFilters(prev => ({...prev, status: v === 'any' ? '' : v}))}
                  >
                    <SelectTrigger className="rounded-full">
                      <SelectValue placeholder="Any status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any status</SelectItem>
                      <SelectItem value="created">Created</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="analyzed">Analyzed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#5C5C5C] mb-2 block">Created Before</label>
                  <Input
                    type="date"
                    value={sessionFilters.beforeDate}
                    onChange={(e) => setSessionFilters(prev => ({...prev, beforeDate: e.target.value}))}
                    className="rounded-full"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-[#5C5C5C] mb-2 block">User</label>
                  <Select 
                    value={sessionFilters.userId} 
                    onValueChange={(v) => setSessionFilters(prev => ({...prev, userId: v === 'any' ? '' : v}))}
                  >
                    <SelectTrigger className="rounded-full">
                      <SelectValue placeholder="Any user" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any user</SelectItem>
                      {stats?.users_with_sessions?.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.session_count} sessions)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button
                  variant="destructive"
                  onClick={handleDeleteSessions}
                  disabled={operationLoading || (!sessionFilters.status && !sessionFilters.beforeDate && !sessionFilters.userId)}
                  className="rounded-full"
                  data-testid="delete-sessions-btn"
                >
                  {operationLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Delete Matching Sessions
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Collections Tab */}
        <TabsContent value="collections" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name: 'sessions', label: 'Sessions', icon: FileText, count: stats?.collections?.sessions, desc: 'All presentation sessions' },
              { name: 'activity_logs', label: 'Activity Logs', icon: Activity, count: stats?.collections?.activity_logs, desc: 'System activity records' },
              { name: 'access_logs', label: 'Access Logs', icon: Shield, count: stats?.collections?.access_logs, desc: 'User access records' },
              { name: 'llm_usage', label: 'LLM Usage', icon: Zap, count: stats?.collections?.llm_usage, desc: 'AI API usage records' }
            ].map(collection => (
              <Card key={collection.name} className="border-none shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-3 rounded-xl bg-gray-100">
                        <collection.icon className="w-6 h-6 text-[#5C5C5C]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1A1A1A]">{collection.label}</h3>
                        <p className="text-sm text-[#5C5C5C]">{collection.desc}</p>
                        <p className="text-lg font-bold text-[#1A1A1A] mt-1">{collection.count || 0} records</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setDeleteCollectionDialog(collection)}
                      disabled={collection.count === 0}
                      className="rounded-full text-red-600 hover:bg-red-50"
                      data-testid={`clear-${collection.name}-btn`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Recycle Bin Tab */}
        <TabsContent value="recycle-bin" className="mt-6">
          <Card className="border-none shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Archive className="w-5 h-5" />
                    Recycle Bin
                  </CardTitle>
                  <CardDescription>
                    Items can be restored within 30 days of deletion
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => handlePurgeRecycleBin(false)}
                    disabled={stats?.recycle_bin?.expired === 0}
                    className="rounded-full"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Purge Expired ({stats?.recycle_bin?.expired || 0})
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => setPurgeDialog(true)}
                    disabled={stats?.recycle_bin?.total === 0}
                    className="rounded-full"
                    data-testid="purge-all-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Purge All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filter */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button
                  variant={recycleFilter === "" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setRecycleFilter(""); setRecyclePage(1); }}
                  className="rounded-full"
                >
                  All ({recycleBin.total})
                </Button>
                {Object.entries(recycleBin.breakdown || {}).map(([type, count]) => (
                  <Button
                    key={type}
                    variant={recycleFilter === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setRecycleFilter(type); setRecyclePage(1); }}
                    className="rounded-full"
                  >
                    {type} ({count})
                  </Button>
                ))}
              </div>

              {/* Items List */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {recycleBin.items.length === 0 ? (
                  <div className="text-center py-12 text-[#5C5C5C]">
                    <Archive className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">Recycle bin is empty</p>
                    <p className="text-sm">Deleted items will appear here for 30 days</p>
                  </div>
                ) : (
                  recycleBin.items.map(item => (
                    <div 
                      key={item.id}
                      className={`p-4 rounded-xl border ${item.is_expired ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge variant={item.is_expired ? "destructive" : "secondary"}>
                            {item.collection_name}
                          </Badge>
                          <div>
                            <p className="font-medium text-[#1A1A1A]">
                              {item.data?.name || item.data?.topic_name || item.original_id}
                            </p>
                            <p className="text-xs text-[#5C5C5C]">
                              Deleted: {formatDate(item.deleted_at)}
                              {item.reason && ` • ${item.reason}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className={`text-sm font-medium ${item.is_expired ? 'text-red-600' : 'text-[#5C5C5C]'}`}>
                              {item.is_expired ? 'Expired' : `${item.days_remaining} days left`}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            {item.collection_name === 'users' ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreUser(item.original_id)}
                                className="rounded-full text-green-600 hover:bg-green-50"
                                data-testid={`restore-user-${item.id}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Restore All
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRestoreItem(item.id)}
                                className="rounded-full text-green-600 hover:bg-green-50"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePermanentDelete(item.id)}
                              className="rounded-full text-red-600 hover:bg-red-50"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {recycleBin.pages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRecyclePage(p => Math.max(1, p - 1))}
                    disabled={recyclePage === 1}
                    className="rounded-full"
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-[#5C5C5C]">
                    Page {recyclePage} of {recycleBin.pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setRecyclePage(p => Math.min(recycleBin.pages, p + 1))}
                    disabled={recyclePage === recycleBin.pages}
                    className="rounded-full"
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete User Dialog */}
      <AlertDialog open={!!deleteUserDialog} onOpenChange={() => setDeleteUserDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete User
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteUserDialog?.name}</strong>?
              <br /><br />
              This will also delete:
              <ul className="list-disc list-inside mt-2 text-left">
                <li>{deleteUserDialog?.session_count || 0} presentation sessions</li>
                <li>All activity logs for this user</li>
                <li>All access logs for this user</li>
              </ul>
              <br />
              <span className="text-green-600">Data can be recovered within 30 days from the Recycle Bin.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteUser(deleteUserDialog?.id)}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              disabled={operationLoading}
            >
              {operationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Collection Dialog */}
      <AlertDialog open={!!deleteCollectionDialog} onOpenChange={() => setDeleteCollectionDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Clear {deleteCollectionDialog?.label}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to clear all <strong>{deleteCollectionDialog?.count || 0}</strong> records from {deleteCollectionDialog?.label}?
              <br /><br />
              <span className="text-green-600">Data can be recovered within 30 days from the Recycle Bin.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteCollection(deleteCollectionDialog?.name)}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              disabled={operationLoading}
            >
              {operationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Clear Collection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Full Reset Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Full Database Reset
            </DialogTitle>
            <DialogDescription>
              This will delete ALL data except admin users:
              <ul className="list-disc list-inside mt-2 text-left">
                <li>{stats?.collections?.users - stats?.collections?.admins} non-admin users</li>
                <li>{stats?.collections?.sessions} sessions</li>
                <li>{stats?.collections?.activity_logs} activity logs</li>
                <li>{stats?.collections?.access_logs} access logs</li>
                <li>{stats?.collections?.llm_usage} LLM usage records</li>
              </ul>
              <br />
              <span className="text-green-600">All data will be moved to Recycle Bin for 30-day recovery.</span>
              <br /><br />
              <strong>Enter password to confirm:</strong>
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            placeholder="Enter admin password"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
            className="rounded-full"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleFullReset}
              disabled={operationLoading || !resetPassword}
              className="rounded-full"
            >
              {operationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Reset Database
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Purge All Dialog */}
      <AlertDialog open={purgeDialog} onOpenChange={setPurgeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Purge All Items
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will <strong>permanently delete</strong> all {stats?.recycle_bin?.total || 0} items in the recycle bin.
              <br /><br />
              <span className="text-red-600 font-semibold">This action cannot be undone!</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handlePurgeRecycleBin(true)}
              className="bg-red-600 hover:bg-red-700 rounded-full"
              disabled={operationLoading}
            >
              {operationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Permanently Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
